import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  Routes,
  Route,
} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Components
import CategoryCard from '../../../components/CategoryCard';
import Loading from '../../../components/global/loading/Loading';

// Hooks & Context
import { useCategory } from '../../../context/CategoryContext';
import { useParameter } from '../../../context/ParameterContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { Category } from '../../../models/Category';
import { Parameter } from '../../../models/Parameter';
import AddParameter from './AddParameter';
import EditParameter from './EditParameter';

// Niestandardowy komponent przycisku
const CustomButton = ({
  to,
  onClick,
  className = '',
  variant = 'primary',
  disabled = false,
  children,
}: {
  to?: string;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  children: React.ReactNode;
}) => {
  // Wspólne style bazowe
  const baseStyles = `
    font-medium text-sm rounded-md transition-all duration-200
    flex items-center justify-center gap-2 whitespace-nowrap
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `;

  // Style dla różnych wariantów
  const variantStyles = {
    primary: `bg-primary hover:bg-primary-dark text-white py-2 px-4
              shadow-sm hover:shadow hover:translate-y-[-1px] active:translate-y-[0px]`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4`,
    outline: `border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 px-4`,
  };

  // Łączymy wszystkie style
  const buttonStyles = `${baseStyles} ${variantStyles[variant]}`;

  // Jeśli mamy atrybut "to", renderujemy Link, w przeciwnym razie button
  if (to) {
    return (
      <Link to={to} className={buttonStyles}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} className={buttonStyles}>
      {children}
    </button>
  );
};

function Parameters() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { control } = useForm();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [categoryAncestors, setCategoryAncestors] = useState<Category[]>([]);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [totalParameters, setTotalParameters] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usingSearch, setUsingSearch] = useState<boolean>(false);

  const { getCategories } = useCategory();
  const { getParametersForSingleCategory, getParameters } = useParameter();
  const debouncedQuery = useDebounce(query, 500);

  // Pobieranie parametrów dla kategorii
  const fetchParametersForCategory = useCallback(
    async (categoryId?: number) => {
      setIsLoading(true);
      try {
        if (categoryId) {
          const response = await getParametersForSingleCategory(categoryId);
          setParameters(response);
          setUsingSearch(false);
        } else {
          const globalParams = await getParametersForSingleCategory(0); // 0 oznacza parametry globalne
          setParameters(globalParams);
          setUsingSearch(false);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getParametersForSingleCategory],
  );

  // Pobieranie kategorii
  const fetchCategories = useCallback(
    async (categoryId?: number) => {
      setIsLoading(true);
      try {
        const response = await getCategories(categoryId);
        setCategories(response.categories);
        setCategoryAncestors(response.ancestors);
        setCurrentCategory(response.currentCategory);

        // Automatycznie ładuj parametry dla bieżącej kategorii
        if (categoryId) {
          await fetchParametersForCategory(categoryId);
        } else if (!usingSearch) {
          await fetchParametersForCategory(); // Parametry globalne
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getCategories, fetchParametersForCategory, usingSearch],
  );

  // Wyszukiwanie parametrów
  const searchParametersData = useCallback(
    async (page: number, query: string) => {
      setIsLoading(true);
      try {
        const response = await getParameters(page, 10, query);
        setParameters(response.parameters);
        setTotalParameters(response.totalParameters);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
        setUsingSearch(true);
      } finally {
        setIsLoading(false);
      }
    },
    [getParameters],
  );

  // Efekt dla ID kategorii w URL
  useEffect(() => {
    if (id && !isNaN(Number(id))) {
      fetchCategories(Number(id));
      setShowCategories(false); // Przełącz na parametry po wybraniu kategorii
    } else if (id === undefined) {
      fetchCategories();
    }
  }, [id, fetchCategories]);

  // Efekt dla wyszukiwania
  useEffect(() => {
    if (debouncedQuery !== '') {
      searchParametersData(currentPage, debouncedQuery);
      setShowCategories(false); // Przełącz na parametry podczas wyszukiwania
    }
  }, [debouncedQuery, currentPage, searchParametersData]);

  // Wyczyszczenie wyszukiwania
  const handleClearSearch = () => {
    setQuery('');
    if (id) {
      fetchParametersForCategory(Number(id));
    } else {
      fetchParametersForCategory();
    }
  };

  // Przełączanie między kategoriami i parametrami
  const toggleView = () => {
    setShowCategories(!showCategories);
  };

  // Wybór kategorii
  const handleCategoryClick = (categoryId: number) => {
    setQuery('');
    navigate(`/admin/parametry/${categoryId}`);
    setShowCategories(false); // Przełącz na parametry po wybraniu kategorii
  };

  // Nawigacja po breadcrumbs
  const handleBreadcrumbClick = (categoryId?: number) => {
    setQuery('');
    if (categoryId) {
      navigate(`/admin/parametry/${categoryId}`);
    } else {
      navigate('/admin/parametry');
    }
  };

  // Sprawdź czy aktualnie jesteśmy na stronie dodawania lub edycji
  const isAddingParameter = location.pathname.includes('/dodaj');
  const isEditingParameter = location.pathname.includes('/edytuj');

  // Jeśli jesteśmy na stronie dodawania lub edycji, renderuj odpowiedni komponent
  if (isAddingParameter) {
    return <AddParameter id={id || ''} />;
  }

  if (isEditingParameter) {
    return <EditParameter id={id || ''} />;
  }

  return (
    <div className="flex flex-col items-stretch space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TuneIcon className="text-primary" />
            Parametry produktów
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Zarządzaj parametrami używanymi do opisu i filtrowania produktów
          </p>
        </motion.div>

        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="self-end sm:self-auto flex gap-2"
        >
          <CustomButton
            to={currentCategory ? `dodaj` : `dodaj`}
            variant="primary"
          >
            <AddIcon fontSize="small" />
            <span>
              {currentCategory
                ? `Dodaj parametr dla: ${currentCategory.name}`
                : 'Dodaj parametr globalny'}
            </span>
          </CustomButton>

          <CustomButton
            onClick={toggleView}
            variant="outline"
            className={showCategories ? 'bg-gray-50' : ''}
          >
            {showCategories ? 'Pokaż parametry' : 'Wybierz kategorię'}
          </CustomButton>
        </motion.div>
      </div>

      {/* Search and navigation */}
      <motion.div
        className="grid grid-cols-1 gap-4"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          <Controller
            name="search"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  {...field}
                  placeholder="Szukaj parametrów (nazwa, opis)"
                  className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <ClearIcon
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
                    fontSize="small"
                    onClick={handleClearSearch}
                  />
                )}
              </div>
            )}
          />
        </div>

        {/* Breadcrumb navigation */}
        <motion.nav
          className="flex"
          aria-label="Breadcrumb"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <ol className="flex items-center flex-wrap">
            <li className="flex items-center">
              <CustomButton
                onClick={() => handleBreadcrumbClick()}
                variant="secondary"
                className="py-1.5 px-3"
              >
                <HomeIcon fontSize="small" />
                <span>Parametry globalne</span>
              </CustomButton>
            </li>

            {categoryAncestors.map((category, index) => (
              <li key={category.id} className="flex items-center">
                <NavigateNextIcon
                  className="text-gray-500 mx-1"
                  fontSize="small"
                />
                <CustomButton
                  onClick={() => handleBreadcrumbClick(category.id)}
                  variant={
                    index === categoryAncestors.length - 1
                      ? 'primary'
                      : 'secondary'
                  }
                  className="py-1.5"
                >
                  {category.name}
                </CustomButton>
              </li>
            ))}
          </ol>
        </motion.nav>
      </motion.div>

      {/* Info box about parameter inheritance */}
      {currentCategory && !showCategories && !usingSearch && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3"
        >
          <InfoOutlinedIcon className="text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-700">
              Dziedziczenie parametrów
            </h3>
            <p className="text-sm text-blue-600 mt-1">
              Parametry są dziedziczone po kategoriach nadrzędnych. Wszystkie
              produkty w tej kategorii będą miały zarówno parametry specyficzne
              dla tej kategorii, jak i parametry z kategorii nadrzędnych oraz
              parametry globalne.
            </p>
          </div>
        </motion.div>
      )}

      {/* Status bar */}
      <motion.div
        className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {usingSearch ? (
          <div>
            Wyświetlono{' '}
            <span className="font-semibold">{parameters.length}</span> z{' '}
            <span className="font-semibold">{totalParameters}</span> parametrów
            spełniających kryteria
          </div>
        ) : showCategories ? (
          <div>
            <span>Wybierz kategorię, aby zarządzać jej parametrami</span>
          </div>
        ) : (
          <div>
            {currentCategory ? (
              <div className="flex items-center">
                <span>Parametry dla kategorii:</span>
                <span className="ml-1 font-semibold text-primary">
                  {currentCategory.name}
                </span>
              </div>
            ) : (
              <span>
                Parametry globalne (dostępne dla wszystkich kategorii)
              </span>
            )}
          </div>
        )}

        {usingSearch && totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span>
              Strona {currentPage} z {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() =>
                  currentPage > 1 &&
                  searchParametersData(currentPage - 1, query)
                }
                disabled={currentPage <= 1}
                className={`p-1 rounded ${
                  currentPage <= 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon
                  style={{ fontSize: '0.9rem', transform: 'rotate(180deg)' }}
                />
              </button>
              <button
                onClick={() =>
                  currentPage < totalPages &&
                  searchParametersData(currentPage + 1, query)
                }
                disabled={currentPage >= totalPages}
                className={`p-1 rounded ${
                  currentPage >= totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon style={{ fontSize: '0.9rem' }} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="loading"
            className="flex justify-center py-12"
          >
            <Loading />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="content"
            transition={{ duration: 0.3 }}
          >
            {/* Wyświetlanie kategorii */}
            {showCategories && (
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {categories.length > 0 ? (
                    categories.map((category, index) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="relative"
                      >
                        <CategoryCard
                          category={category}
                          getCategoriesData={fetchCategories}
                          onCategoryClick={handleCategoryClick}
                        />
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <TuneIcon
                        style={{ fontSize: '3rem' }}
                        className="text-gray-300 mb-2"
                      />
                      <h3 className="text-lg font-medium text-gray-700">
                        Brak kategorii
                      </h3>
                      <p className="text-gray-500 mt-1">
                        Dodaj kategorie, aby móc przypisać do nich parametry
                      </p>
                      <CustomButton
                        to="/admin/kategorie"
                        className="mt-4 py-2.5 px-5"
                      >
                        Przejdź do zarządzania kategoriami
                      </CustomButton>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Wyświetlanie parametrów */}
            {!showCategories && (
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {parameters.length > 0 ? (
                    parameters.map((parameter, index) => (
                      <motion.div
                        key={parameter.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="relative"
                      >
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                          <div className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <div className="bg-primary/10 p-2 rounded-lg">
                                  <TuneIcon className="text-primary" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-medium text-gray-800">
                                    {parameter.name}
                                  </h3>
                                  {parameter.description && (
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                      {parameter.description}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                <Link
                                  to={`/admin/parametry/edytuj/${parameter.id}`}
                                  className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white py-1.5 px-3 rounded transition-colors text-sm font-medium"
                                >
                                  <TuneIcon fontSize="small" />
                                  <span>Edytuj</span>
                                </Link>
                              </div>
                            </div>
                          </div>

                          {/* Informacja o źródle parametru (tylko dla wyszukiwania) */}
                          {usingSearch && parameter.category && (
                            <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex justify-between items-center text-sm">
                              <span className="text-gray-500">
                                Kategoria:{' '}
                                <button
                                  onClick={() =>
                                    handleCategoryClick(parameter.category.id)
                                  }
                                  className="font-semibold text-primary hover:underline"
                                >
                                  {parameter.category.name}
                                </button>
                              </span>
                              <span className="text-gray-500">
                                {parameter.categoryId === null
                                  ? 'Parametr globalny'
                                  : ''}
                              </span>
                            </div>
                          )}

                          {/* Dodatkowe informacje o typie parametru */}
                          {!usingSearch && (
                            <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex justify-between items-center text-sm">
                              <span className="text-gray-500">
                                Typ:
                                <span className="ml-1 font-medium">
                                  {parameter.categoryId === null
                                    ? 'Parametr globalny'
                                    : currentCategory &&
                                        parameter.categoryId !==
                                          currentCategory.id
                                      ? 'Odziedziczony z kategorii nadrzędnej'
                                      : 'Własny parametr kategorii'}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <TuneIcon
                        style={{ fontSize: '3rem' }}
                        className="text-gray-300 mb-2"
                      />
                      <h3 className="text-lg font-medium text-gray-700">
                        {usingSearch
                          ? 'Nie znaleziono pasujących parametrów'
                          : currentCategory
                            ? 'Ta kategoria nie ma własnych parametrów'
                            : 'Brak parametrów globalnych'}
                      </h3>
                      <p className="text-gray-500 mt-1">
                        {usingSearch
                          ? 'Spróbuj użyć innych słów kluczowych w wyszukiwaniu'
                          : 'Dodaj parametry, aby opisać produkty i umożliwić filtrowanie'}
                      </p>
                      <CustomButton to="dodaj" className="mt-4 py-2.5 px-5">
                        <AddIcon fontSize="small" />
                        <span>
                          {currentCategory
                            ? `Dodaj parametr dla: ${currentCategory.name}`
                            : 'Dodaj parametr globalny'}
                        </span>
                      </CustomButton>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Paginacja dla wyników wyszukiwania */}
            {usingSearch && totalPages > 1 && (
              <div className="flex justify-center py-6">
                <div className="flex items-center gap-1">
                  <CustomButton
                    onClick={() => searchParametersData(1, query)}
                    disabled={currentPage === 1}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === 1 ? 'opacity-50' : ''
                    }`}
                  >
                    Pierwsza
                  </CustomButton>

                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={idx}
                        onClick={() => searchParametersData(pageNum, query)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                          ${
                            currentPage === pageNum
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <CustomButton
                    onClick={() => searchParametersData(totalPages, query)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === totalPages ? 'opacity-50' : ''
                    }`}
                  >
                    Ostatnia
                  </CustomButton>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Główny komponent routingu
function ParametersRouter() {
  return (
    <Routes>
      <Route path="dodaj" element={<AddParameter id="" />} />
      <Route path=":id/dodaj" element={<Parameters />} />
      <Route path="edytuj/:id" element={<Parameters />} />
      <Route path=":id" element={<Parameters />} />
      <Route path="" element={<Parameters />} />
    </Routes>
  );
}

export default ParametersRouter;
